import { nanoid } from 'nanoid'
import React from 'react'
import { useTranslation } from 'react-i18next'

import { HStack } from '~/components/elements/HStack'
import { LabelingContainer } from '~/components/elements/LabelingContainer'
import { StyledButton } from '~/components/elements/StyledButton'
import { StyledInput } from '~/components/elements/StyledInput'
import { Typography } from '~/components/elements/Typography'
import { VStack } from '~/components/elements/VStack'
import { useLogEvent } from '~/hooks/useLogEvent'
import { useSurveyQueryData } from '~/hooks/useSurveyQuery/useSurveyQueryData'
import { exists, type SurveyFilterGroups, truncate } from '~/utils'
import { buildSurveyFilterTextGroups } from '~/utils/specifics/buildSurveyFilterTextGroups'

import { type FilterPatternDraft } from '../atoms'
import { useBasicOutputWizard } from '../hooks'

import { EditFilterPatternDialog } from './EditFilterPatternDialog'

type FilterPatternStepProps = {
  onBack: () => void
  onNext: () => void
}

export const FilterPatternStep = ({
  onBack,
  onNext,
}: FilterPatternStepProps) => {
  const { filterPatternDrafts, isFilterPatternsValid, setFilterPatternDrafts } =
    useBasicOutputWizard()
  const survey = useSurveyQueryData()
  const { t } = useTranslation()
  const logEvent = useLogEvent()

  const [editingId, setEditingId] = React.useState<string | null>(null)

  const filterSummaryText = React.useMemo(() => {
    if (!survey) {
      return new Map<string, string>()
    }
    const summaryMap = new Map<string, string>()
    filterPatternDrafts.forEach((draft) => {
      if (draft.filterGroups.length === 0) {
        summaryMap.set(draft.id, t('basic_output_confirm_filter_all_data'))
      } else {
        const filterTextGroups = buildSurveyFilterTextGroups({
          brandGroups: survey.brandGroups,
          customerAttributeGroups: survey.customerAttributeGroups,
          filterGroups: draft.filterGroups,
          questions: survey.questions,
          t,
        })
        const text = filterTextGroups
          .map((group) =>
            group
              .map(
                (ft) =>
                  `${[ft.name, ft.subName].filter(exists).join(' ')}: ${ft.values.join(', ')}`,
              )
              .join(' AND '),
          )
          .join(' OR ')
        summaryMap.set(draft.id, truncate(text, 60))
      }
    })

    return summaryMap
  }, [filterPatternDrafts, survey, t])

  const handleNameChange = React.useCallback(
    (id: string, name: string) => {
      const updated = filterPatternDrafts.map((draft) =>
        draft.id === id ? { ...draft, name } : draft,
      )
      setFilterPatternDrafts(updated)
    },
    [filterPatternDrafts, setFilterPatternDrafts],
  )

  const handleAdd = React.useCallback(() => {
    const newDraft: FilterPatternDraft = {
      filterGroups: [],
      id: nanoid(),
      name: '',
    }
    setFilterPatternDrafts([...filterPatternDrafts, newDraft])
    logEvent('Add filter pattern')
  }, [filterPatternDrafts, logEvent, setFilterPatternDrafts])

  const handleRemove = React.useCallback(
    (id: string) => {
      setFilterPatternDrafts(filterPatternDrafts.filter((d) => d.id !== id))
      setEditingId((prev) => (prev === id ? null : prev))
    },
    [filterPatternDrafts, setFilterPatternDrafts],
  )

  const handleFilterConfirm = React.useCallback(
    (filterGroups: SurveyFilterGroups) => {
      if (editingId === null) {
        return
      }
      const updated = filterPatternDrafts.map((draft) =>
        draft.id === editingId ? { ...draft, filterGroups } : draft,
      )
      setFilterPatternDrafts(updated)
      setEditingId(null)
    },
    [editingId, filterPatternDrafts, setFilterPatternDrafts],
  )

  const handleFilterClose = React.useCallback(() => {
    setEditingId(null)
  }, [])

  const editingDraft = filterPatternDrafts.find((d) => d.id === editingId)

  return (
    <VStack spacing={4}>
      <Typography variant="normal">{t('basic_output_filter_intro')}</Typography>

      {filterPatternDrafts.map((draft, index) => (
        <VStack key={draft.id} spacing={2}>
          <HStack align="end" spacing={2}>
            <LabelingContainer
              label={t('basic_output_filter_default_name', {
                index: index + 1,
              })}
            >
              <StyledInput
                placeholder={t('basic_output_filter_name_placeholder')}
                value={draft.name}
                width="100%"
                onValueChange={(name) => handleNameChange(draft.id, name)}
              />
            </LabelingContainer>
            <StyledButton
              buttonStyle="normal"
              onClick={() => setEditingId(draft.id)}
            >
              {t('basic_output_filter_edit_button')}
            </StyledButton>
            {filterPatternDrafts.length >= 2 && (
              <StyledButton
                buttonStyle="light"
                onClick={() => handleRemove(draft.id)}
              >
                {t('basic_output_remove')}
              </StyledButton>
            )}
          </HStack>
          <Typography variant="normal">
            {t('basic_output_filter_summary_prefix', {
              summary:
                filterSummaryText.get(draft.id) ??
                t('basic_output_confirm_filter_all_data'),
            })}
          </Typography>
        </VStack>
      ))}

      <HStack spacing={2}>
        <StyledButton buttonStyle="normal" onClick={handleAdd}>
          {t('basic_output_filter_add_button')}
        </StyledButton>
      </HStack>

      <HStack spacing={2}>
        <StyledButton buttonStyle="normal" onClick={onBack}>
          {t('basic_output_back')}
        </StyledButton>
        <StyledButton
          buttonStyle="success"
          disabled={!isFilterPatternsValid}
          onClick={onNext}
        >
          {t('basic_output_next')}
        </StyledButton>
      </HStack>

      {editingDraft && (
        <EditFilterPatternDialog
          filterGroups={editingDraft.filterGroups}
          onClose={handleFilterClose}
          onConfirm={handleFilterConfirm}
        />
      )}
    </VStack>
  )
}
